import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { PMButton, PMDataTable, PMEmptyState, PMFilterBar, PMSegmented, PMSkeleton, PMTabs } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { levelDef } from "@/lib/levels";
import { Page } from "@/ui/Page";
import { Kpi } from "@/ui/Kpi";
import { Character } from "@/ui/Character";
import { shortDate } from "@/ui/format";
import { formatBytes, usePaperTrail, type FileKind, type TrailRow } from "./queries";
import { FileTile, KIND_LABEL, useOpenFile } from "./files";
import "./clients.css";

const TYPES: { id: string; label: string; match: (k: FileKind) => boolean }[] = [
  { id: "all", label: "All files", match: () => true },
  { id: "report", label: "Signed reports", match: (k) => k === "report" },
  { id: "pdf", label: "PDFs", match: (k) => k === "pdf" },
  { id: "image", label: "Images", match: (k) => k === "image" },
  { id: "sheet", label: "Spreadsheets", match: (k) => k === "sheet" },
  { id: "doc", label: "Docs", match: (k) => k === "doc" || k === "other" },
];

// Paper Trail (core-features 4.5): every document linked to the location it
// belongs to. Visit attachments, farm-report files, and signed visit reports.
export function PaperTrailPage() {
  const { teamId } = useParams({ from: "/t/$teamId/clients/paper-trail" });
  const search = useSearch({ from: "/t/$teamId/clients/paper-trail" });
  const nav = useNavigate();
  const { hierarchy } = useApp();
  const trail = usePaperTrail();
  const open = useOpenFile();
  const set = (patch: Record<string, string | undefined>) => nav({ to: ".", search: { ...search, ...patch }, replace: true } as any);
  const type = search.type || "all";
  const q = (search.q || "").trim().toLowerCase();

  const locations = useMemo(() => {
    const m = new Map<string, { id: string; label: string; level: string | null }>();
    for (const r of trail.data) if (r.locationId && !m.has(r.locationId)) m.set(r.locationId, { id: r.locationId, label: r.locationLabel || "Untitled", level: r.locationLevel });
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [trail.data]);

  const base = trail.data.filter((r) => (!search.location || r.locationId === search.location) && (!q || `${r.name} ${r.locationLabel} ${r.visitTitle}`.toLowerCase().includes(q)));
  const rows = base.filter((r) => TYPES.find((t) => t.id === type)!.match(r.type));
  const counts = Object.fromEntries(TYPES.map((t) => [t.id, base.filter((r) => t.match(r.type)).length]));
  const loc = locations.find((l) => l.id === search.location);
  const [view, setView] = useState<"list" | "grouped">("list");
  const byLoc = new Map<string, TrailRow[]>();
  for (const r of rows) { const k = r.locationLabel || "Unlinked"; byLoc.set(k, [...(byLoc.get(k) || []), r]); }
  const groups = [...byLoc.entries()].sort((a, b) => a[0].localeCompare(b[0]));

  const table = (list: TrailRow[]) => (
    <PMDataTable
      rowKey="id"
      rows={list}
      onRowClick={(r: TrailRow) => open(r)}
      emptyText="No files match"
      columns={[
        { key: "name", label: "File", sortable: true, render: (r: TrailRow) => <span className="trail-name"><FileTile kind={r.type} size={30} /><span><strong>{r.name}</strong><small>{r.reportId ? `Signed by ${r.approver ?? "approver"}` : KIND_LABEL[r.type].replace(/s$/, "")}</small></span></span> },
        { key: "locationLabel", label: "Location", sortable: true, render: (r: TrailRow) => r.locationId ? <button className="linkbtn trail-link" onClick={(e) => { e.stopPropagation(); nav({ to: "/t/$teamId/locations/$locationId", params: { teamId, locationId: r.locationId! } }); }}>{levelDef(r.locationLevel, hierarchy.key).emoji} {r.locationLabel}</button> : <span className="subtle">—</span> },
        { key: "visitTitle", label: "Visit", render: (r: TrailRow) => r.visitId ? <button className="linkbtn trail-link" onClick={(e) => { e.stopPropagation(); nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: r.visitId! }, search: r.reportId ? { tab: "report" } : {} } as any); }}>{r.visitTitle || "Field visit"}</button> : <span className="subtle">—</span> },
        { key: "size", label: "Size", align: "right", sortable: true, render: (r: TrailRow) => <span className="muted">{r.reportId ? "PDF" : formatBytes(r.size)}</span>, sortValue: (r: TrailRow) => r.size ?? 0 },
        { key: "date", label: "Added", sortable: true, render: (r: TrailRow) => shortDate(r.date), sortValue: (r: TrailRow) => r.date },
        { key: "open", label: "", align: "right", render: (r: TrailRow) => <PMButton variant="text" size="sm" icon={r.reportId ? "download" : "external-link"} onClick={(e: any) => { e.stopPropagation(); open(r); }}>{r.reportId ? "Download" : "Open"}</PMButton> },
      ]}
    />
  );

  return (
    <Page
      chip="MAPS & DATA"
      title="Paper Trail"
      subtitle={`${trail.data.length} ${trail.data.length === 1 ? "document" : "documents"} across ${locations.length} ${locations.length === 1 ? "location" : "locations"}`}
      actions={<div className="page-toolbar">
        <PMSegmented value={view} onChange={(v: string) => setView(v === "grouped" ? "grouped" : "list")} options={[{ value: "list", label: "List" }, { value: "grouped", label: "By location" }]} />
        <span title="Uploads arrive with the web API"><PMButton icon="cloud-upload" disabled>Upload</PMButton></span>
      </div>}
      wide
    >
      <div className="kpis">
        <Kpi emoji="🗂️" tone="tone-note" value={trail.data.length} label="Documents" />
        <Kpi emoji="✍️" tone="tone-brand" value={trail.data.filter((r) => r.type === "report").length} label="Signed reports" />
        <Kpi emoji="📎" tone="tone-photo" value={trail.data.filter((r) => r.kind === "file").length} label="Attachments" />
        <Kpi emoji="📍" tone="tone-text" value={locations.length} label="Locations with files" />
      </div>

      <PMTabs value={type} onChange={(id: string) => set({ type: id === "all" ? undefined : id })} tabs={TYPES.map((t) => ({ id: t.id, label: t.label, count: counts[t.id] }))} aria-label="File type" />
      <PMFilterBar
        search={search.q || ""}
        searchPlaceholder="Search file names, locations, visits"
        onSearch={(v: string) => set({ q: v || undefined })}
        filters={[{ key: "location", label: "Location", icon: "map-pin-outline", searchable: true, value: search.location, options: locations.map((l) => ({ value: l.id, label: `${levelDef(l.level, hierarchy.key).emoji} ${l.label}` })), onChange: (v: string | string[]) => set({ location: (v as string) || undefined }) }]}
        activeChips={loc ? [{ key: "location", label: loc.label }] : []}
        onRemoveChip={() => set({ location: undefined })}
        onClearAll={() => set({ location: undefined, q: undefined, type: undefined })}
      />

      {trail.isLoading && !trail.data.length ? <PMSkeleton shape="rect" height={360} radius={16} />
        : !trail.data.length ? <Character name="farmer" size={110} title="No documents yet" line="Files attached to visits and signed reports collect here, linked to their location." />
        : !rows.length ? <PMEmptyState title="No files match" description="Try another type, location or search." action={<PMButton variant="text" onClick={() => set({ location: undefined, q: undefined, type: undefined })}>Clear filters</PMButton>} />
        : view === "grouped" ? (
          groups.map(([label, list]) => (
            <section key={label} className="trail-group">
              <div className="eyebrow"><span>{label}</span><span className="count-pill">{list.length}</span></div>
              <div className="card">{table(list)}</div>
            </section>
          ))
        ) : <div className="card">{table(rows)}</div>}
    </Page>
  );
}
